/**
 * Registrations API - For handling pending user registrations
 */
import { apiClient } from "./client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface CountyDto {
    id: string;
    code: string;
    name: string;
}

export interface MunicipalityDto {
    id: string;
    name: string;
    code: string;
}

export interface RegistrationSummary {
    id: string;
    email: string;
    userName: string;
    requestedTeamId: string;
    requestedRole: string;
    isProcessed: boolean;
    isApproved: boolean;
    createdAt: string;
    expiresAt: string;
}

export interface RegisterRequest {
    email: string;
    userName: string;
    password: string;
    requestedMunicipalityPolygonId: string;
    requestedRole: number; // SystemRole enum value
}

export interface RegisterResponse {
    id: string;
    expiresAt: string;
}

export interface ApproveRegistrationRequest {
    registrationId: string;
    approvalNotes?: string;
}

export interface RejectRegistrationRequest {
    registrationId: string;
    reason: string;
}

/**
 * Fetches all counties (län) for registration municipality filter (public endpoint)
 */
export const fetchCountiesForRegistration = async (): Promise<CountyDto[]> => {
    const response = await apiClient.get<CountyDto[]>("/registrations/counties");
    return response.data;
};

/**
 * Fetches municipalities (kommuner) for registration, optionally filtered by county (public endpoint)
 */
export const fetchMunicipalitiesForRegistration = async (countyId?: string | null): Promise<MunicipalityDto[]> => {
    const params = countyId ? { countyId } : {};
    const response = await apiClient.get<MunicipalityDto[]>("/registrations/municipalities", { params });
    return response.data;
};

/**
 * Registers a new user
 */
export const registerUser = async (request: RegisterRequest): Promise<RegisterResponse> => {
    const response = await apiClient.post<RegisterResponse>("/registrations", request);
    return response.data;
};

/**
 * Fetches all pending registrations
 */
export const fetchPendingRegistrations = async (): Promise<RegistrationSummary[]> => {
    const response = await apiClient.get<RegistrationSummary[]>("/admin/registrations/pending");
    return response.data;
};

/**
 * Approves a registration
 */
export const approveRegistration = async (request: ApproveRegistrationRequest): Promise<void> => {
    await apiClient.post("/admin/registrations/approve", request);
};

/**
 * Rejects a registration
 */
export const rejectRegistration = async (request: RejectRegistrationRequest): Promise<void> => {
    await apiClient.post("/admin/registrations/reject", request);
};

/**
 * React Query hook for fetching pending registrations
 */
export const usePendingRegistrations = () => {
    return useQuery({
        queryKey: ["registrations", "pending"],
        queryFn: fetchPendingRegistrations,
        staleTime: 30 * 1000, // 30 seconds (frequently updated)
        refetchInterval: 60 * 1000, // Refetch every minute
    });
};

/**
 * React Query hook for approving a registration
 */
export const useApproveRegistration = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: approveRegistration,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["registrations", "pending"] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
    });
};

/**
 * React Query hook for rejecting a registration
 */
export const useRejectRegistration = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: rejectRegistration,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["registrations", "pending"] });
        },
    });
};

/**
 * React Query hook for fetching counties for registration
 */
export const useCountiesForRegistration = () => {
    return useQuery({
        queryKey: ["registrations", "counties"],
        queryFn: fetchCountiesForRegistration,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * React Query hook for fetching municipalities for registration
 */
export const useMunicipalitiesForRegistration = (countyId?: string | null) => {
    return useQuery({
        queryKey: ["registrations", "municipalities", countyId ?? "all"],
        queryFn: () => fetchMunicipalitiesForRegistration(countyId ?? undefined),
        staleTime: 5 * 60 * 1000,
        enabled: true,
    });
};

/**
 * React Query hook for user registration
 */
export const useRegisterUser = () => {
    return useMutation({
        mutationFn: registerUser,
    });
};